import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format, addDays, startOfDay, isSameDay, isToday } from 'date-fns';
import { toast } from 'sonner';
import { ArrowLeft, Check, Clock, Users, Loader2, Repeat, Timer } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DURATION_OPTIONS = [
  { slots: 1, label: '15m' },
  { slots: 2, label: '30m' },
  { slots: 3, label: '45m' },
  { slots: 4, label: '1h' },
  { slots: 6, label: '1.5h' },
  { slots: 8, label: '2h' },
  { slots: 12, label: '3h' },
  { slots: 16, label: '4h' },
];

function formatDuration(minutes) {
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h${m}m` : `${h}h`;
}

function addMinutesToSlot(slot, minutes) {
  const [h, m] = slot.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${Math.floor(total / 60).toString().padStart(2, '0')}:${(total % 60).toString().padStart(2, '0')}`;
}

export default function BookingPage() {
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledDate = searchParams.get('date');
  const prefilledTime = searchParams.get('time');
  const savedTimeRef = useRef(prefilledTime || null);

  const [resource, setResource] = useState(null);
  const [selectedDate, setSelectedDate] = useState(prefilledDate ? startOfDay(new Date(prefilledDate + 'T00:00:00')) : startOfDay(new Date()));
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [durationSlots, setDurationSlots] = useState(1);
  const [recurringWeeks, setRecurringWeeks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const days = Array.from({ length: 14 }, (_, i) => addDays(startOfDay(new Date()), i));

  useEffect(() => {
    axios.get(`${API}/api/resources/${resourceId}`, { withCredentials: true })
      .then(res => setResource(res.data))
      .catch(() => toast.error('Resource not found'))
      .finally(() => setLoading(false));
  }, [resourceId]);

  const fetchSlots = useCallback(async () => {
    setSlotsLoading(true);
    setSelectedSlot(savedTimeRef.current || null);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const { data } = await axios.get(`${API}/api/bookings/available-slots`, {
        params: { resource_id: resourceId, date: dateStr },
        withCredentials: true,
      });
      setSlots(data.slots);
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [selectedDate, resourceId]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  // Build a lookup from slot time to index
  const slotIndex = useMemo(() => {
    const m = {};
    slots.forEach((s, i) => { m[s.time] = i; });
    return m;
  }, [slots]);

  const durationMinutes = durationSlots * 15;

  // Check if a slot can be a valid start for the selected duration
  const canStartAt = useCallback((slotTime) => {
    const idx = slotIndex[slotTime];
    if (idx === undefined) return false;
    for (let i = 0; i < durationSlots; i++) {
      const s = slots[idx + i];
      if (!s || !s.available) return false;
    }
    return true;
  }, [slotIndex, slots, durationSlots]);

  // Get the set of slots that would be booked
  const selectedSlotSet = useMemo(() => {
    if (!selectedSlot) return new Set();
    const idx = slotIndex[selectedSlot];
    if (idx === undefined) return new Set();
    const set = new Set();
    for (let i = 0; i < durationSlots; i++) {
      if (slots[idx + i]) set.add(slots[idx + i].time);
    }
    return set;
  }, [selectedSlot, slotIndex, slots, durationSlots]);

  const multiSlotValid = selectedSlot ? canStartAt(selectedSlot) : false;
  const endTime = selectedSlot ? addMinutesToSlot(selectedSlot, durationMinutes) : '';

  const handleBook = async () => {
    if (!selectedSlot || !multiSlotValid) return;
    setBooking(true);
    try {
      const { data } = await axios.post(`${API}/api/bookings`, {
        resource_id: resourceId,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedSlot,
        duration_slots: durationSlots,
        recurring_weeks: recurringWeeks,
      }, { withCredentials: true });
      setConfirmed(data);
      toast.success('Booking confirmed!');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Booking failed';
      toast.error(typeof msg === 'string' ? msg : 'Booking failed');
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={24} className="animate-spin text-text-secondary" />
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-text-secondary">Resource not found.</p>
        <Link to="/" className="text-white underline mt-4 inline-block">Back to resources</Link>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="booking-confirmed">
        <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-available/10 border border-available/30">
          <Check size={28} className="text-available" />
        </div>
        <h2 className="font-heading text-2xl font-medium tracking-tight text-white mb-2">Booking Confirmed</h2>
        <p className="text-text-secondary font-body text-sm mb-1">{resource.name}</p>
        <p className="font-mono text-sm text-white mb-1">
          {format(selectedDate, 'EEEE, MMMM d, yyyy')}
        </p>
        <p className="font-mono text-sm text-white mb-1">
          {selectedSlot} — {endTime} ({formatDuration(durationMinutes)})
        </p>
        {recurringWeeks > 0 && (
          <p className="text-text-secondary text-xs font-mono mt-1">
            Recurring weekly for {recurringWeeks} weeks ({confirmed.total_slots_booked} slots total)
          </p>
        )}
        <p className="text-text-secondary text-xs font-body mt-3 mb-8">
          A confirmation email has been sent. You'll get a reminder 30 min before.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate('/my-bookings')}
            data-testid="view-bookings-button"
            className="px-6 py-2.5 bg-white text-black text-sm font-body font-medium hover:opacity-90 transition-all"
          >
            View My Bookings
          </button>
          <button
            onClick={() => { setConfirmed(null); setSelectedSlot(null); setDurationSlots(1); setRecurringWeeks(0); fetchSlots(); }}
            data-testid="book-another-button"
            className="px-6 py-2.5 border border-border text-white text-sm font-body hover:bg-surface transition-all"
          >
            Book Another Slot
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="booking-page">
      <Link to="/" className="inline-flex items-center gap-2 text-text-secondary hover:text-white text-sm font-body mb-6 transition-colors" data-testid="back-to-resources">
        <ArrowLeft size={16} />
        Back to resources
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tighter text-white" data-testid="booking-resource-name">
            {resource.name}
          </h1>
          <p className="text-text-secondary text-sm font-body mt-1">{resource.description}</p>
        </div>
        <div className="flex items-center gap-1.5 text-text-secondary shrink-0">
          <Users size={14} strokeWidth={1.5} />
          <span className="text-xs font-mono">{resource.capacity} {resource.capacity === 1 ? 'seat' : 'seats'}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Date Picker */}
        <div className="lg:col-span-1" data-testid="date-picker-section">
          <h3 className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary mb-4">Select Date</h3>
          <div className="grid grid-cols-7 lg:grid-cols-2 gap-2">
            {days.map(day => {
              const active = isSameDay(day, selectedDate);
              const today_ = isToday(day);
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelectedDate(day)}
                  data-testid={`date-${format(day, 'yyyy-MM-dd')}`}
                  className={`p-2 lg:p-3 text-center border transition-all duration-200 ${
                    active
                      ? 'bg-white text-black border-white'
                      : 'bg-surface border-border/40 text-text-secondary hover:text-white hover:border-white/30'
                  }`}
                >
                  <div className="text-[10px] font-mono uppercase">{format(day, 'EEE')}</div>
                  <div className={`text-lg font-heading font-medium ${active ? 'text-black' : ''}`}>{format(day, 'd')}</div>
                  <div className="text-[10px] font-mono">{format(day, 'MMM')}</div>
                  {today_ && <div className={`w-1 h-1 mx-auto mt-1 rounded-full ${active ? 'bg-black' : 'bg-accent'}`} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Slots + Options */}
        <div className="lg:col-span-3" data-testid="time-slots-section">
          {/* Duration & Recurring */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1" data-testid="duration-picker">
              <h3 className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary mb-3 flex items-center gap-1.5">
                <Timer size={12} /> Duration
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {DURATION_OPTIONS.map(opt => (
                  <button
                    key={opt.slots}
                    onClick={() => { setDurationSlots(opt.slots); setSelectedSlot(null); }}
                    data-testid={`duration-${opt.slots}`}
                    className={`px-3 py-1.5 text-center border text-xs font-mono transition-all duration-200 ${
                      durationSlots === opt.slots
                        ? 'bg-white text-black border-white'
                        : 'bg-surface border-border/40 text-text-secondary hover:text-white hover:border-white/30'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1" data-testid="recurring-picker">
              <h3 className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary mb-3 flex items-center gap-1.5">
                <Repeat size={12} /> Repeat Weekly
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {[0, 2, 4, 8, 12].map(w => (
                  <button
                    key={w}
                    onClick={() => setRecurringWeeks(w)}
                    data-testid={`recurring-${w}`}
                    className={`px-3 py-1.5 text-center border text-xs font-mono transition-all duration-200 ${
                      recurringWeeks === w
                        ? 'bg-white text-black border-white'
                        : 'bg-surface border-border/40 text-text-secondary hover:text-white hover:border-white/30'
                    }`}
                  >
                    {w === 0 ? 'Once' : `${w}w`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary">
              {format(selectedDate, 'EEEE, MMM d')} — Time Slots (15 min)
            </h3>
            <Clock size={14} className="text-text-secondary" />
          </div>

          {slotsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={20} className="animate-spin text-text-secondary" />
            </div>
          ) : (
            <div data-testid="time-slots-dropdown">
              <select
                value={selectedSlot || ''}
                onChange={e => setSelectedSlot(e.target.value || null)}
                className="w-full bg-surface border border-border/40 text-white text-sm font-mono px-4 py-3 focus:outline-none focus:border-white/40 appearance-none cursor-pointer"
                data-testid="time-slot-select"
              >
                <option value="">Select a time slot</option>
                {slots.map(slot => {
                  const valid = slot.available && canStartAt(slot.time);
                  return (
                    <option
                      key={slot.time}
                      value={slot.time}
                      disabled={!valid}
                      data-testid={`slot-option-${slot.time}`}
                      className={!slot.available ? 'text-text-secondary/30' : 'text-white'}
                    >
                      {slot.time} {!slot.available ? '(unavailable)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Confirm */}
          {selectedSlot && multiSlotValid && (
            <div className="mt-6 p-4 border border-border/40 bg-surface" data-testid="booking-summary">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-mono text-text-secondary uppercase tracking-wider">Confirm Booking</p>
                  <p className="font-heading text-white text-lg mt-1">
                    {format(selectedDate, 'MMM d')} <span className="font-mono">{selectedSlot} — {endTime}</span>
                    <span className="text-text-secondary text-sm ml-2">({formatDuration(durationMinutes)})</span>
                  </p>
                  {recurringWeeks > 0 && (
                    <p className="text-xs font-mono text-accent mt-1">
                      Repeats weekly for {recurringWeeks} weeks ({recurringWeeks * durationSlots} total slots)
                    </p>
                  )}
                </div>
                <button
                  onClick={handleBook}
                  disabled={booking}
                  data-testid="confirm-booking-button"
                  className="px-6 py-2.5 bg-white text-black font-body font-medium text-sm hover:opacity-90 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 flex items-center gap-2 shrink-0"
                >
                  {booking ? <Loader2 size={16} className="animate-spin" /> : <>Confirm <Check size={16} /></>}
                </button>
              </div>
            </div>
          )}

          {selectedSlot && !multiSlotValid && (
            <div className="mt-6 p-3 border border-pending/30 bg-pending/10 text-pending text-sm font-body" data-testid="slot-conflict-warning">
              Not enough consecutive available slots for {formatDuration(durationMinutes)}. Try a shorter duration or different time.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
